import { datadogRum } from '@datadog/browser-rum'
import debug from '../utils/console'
import { GROUP_COOKIE } from '../openfeature/cacheKey'
import {
  createHydrationMismatchCounter,
  hydrationMismatchLocation,
  hydrationMismatchSpot,
  LOCATION_LENGTH,
  mountedInSpot,
  type MismatchSpot,
} from '../utils/render/hydrationMismatches'

const logger = debug.withTag('hydration-mismatch')

/**
 * What Vue logged, as one string. The element it names is an object argument,
 * while the message and the rendered against expected detail are strings, and
 * those are the parts worth carrying to Datadog.
 */
function textOf(args: unknown[]): string {
  return args.filter((arg) => typeof arg === 'string').join(' ')
}

/**
 * The second canary on the group cache. plugins/ssr-flag-check.client.ts only
 * compares feature flags, so it is blind to every other input a render reads
 * straight from the browser or the request: a localStorage setting, the request
 * geo, a fresh random id, state a module kept between requests. Any of those
 * makes the server draw something the browser then disagrees with, and Vue
 * calls that a hydration mismatch.
 *
 * So the count of them is the signal. It has to come off the browser console,
 * because `app.config.warnHandler` is never reached in a production build: Vue
 * only pushes the component that would find the handler onto its warning stack
 * in development, and with nothing on that stack the warning goes straight to
 * the console. The line Vue would have printed is printed either way.
 *
 * Every rendered view carries the count into RUM next to `rendered` and
 * `group`, and anything above zero is also reported to /api/ssr-flag-check,
 * which is the signal to alarm on: RUM samples a fraction of a percent of
 * sessions and this beacon samples none.
 */
export default defineNuxtPlugin({
  name: 'hydration-mismatch',
  // Before hydration starts, which is the only window the count exists in.
  enforce: 'pre',
  setup(nuxtApp) {
    if (!nuxtApp.payload.serverRendered) return
    const counter = createHydrationMismatchCounter()
    // Read here rather than in the callback below: onNuxtReady runs outside the
    // Nuxt instance, and useCookie throws once it is gone.
    const group = useCookie(GROUP_COOKIE).value || 'none'

    // `console` is an auto import in this app, common/utils/console/index.ts
    // under its directory name, so a bare console.warn here would wrap consola
    // and never see the line Vue writes. This is the browser's own console.
    const browserConsole = window.console
    const previousWarn = browserConsole.warn
    // Where the first mismatch sits, so a report names the component to fix.
    let where: string | undefined
    let spot: MismatchSpot | undefined
    const countingWarn = (...args: unknown[]): void => {
      if (counter.recordWarning(textOf(args)) && where === undefined) {
        where = hydrationMismatchLocation(args)
        spot = hydrationMismatchSpot(args)
      }
      previousWarn(...args)
    }
    // Vue logs this one whatever the build compiled in, so a tenant without the
    // detailed warnings still reports its mismatch, as a count of one.
    const previousError = browserConsole.error
    const countingError = (...args: unknown[]): void => {
      counter.recordSummary(args[0])
      previousError(...args)
    }
    browserConsole.warn = countingWarn
    browserConsole.error = countingError

    onNuxtReady(() => {
      // Hydration is over, so the patches have nothing left to catch. Only ours
      // comes back off: anything that wrapped it since owns the console now.
      if (browserConsole.warn === countingWarn) {
        browserConsole.warn = previousWarn
      }
      if (browserConsole.error === countingError) {
        browserConsole.error = previousError
      }

      const count = counter.total()
      datadogRum.setViewContextProperty('hydrationMismatches', count)
      if (count === 0) return

      // Vue has mounted its own render in the mismatch's place by now, and that
      // element is usually what names the component.
      const mounted = spot ? mountedInSpot(spot) : undefined
      if (
        where &&
        mounted &&
        where.length + mounted.length <= LOCATION_LENGTH
      ) {
        where = `${where}${mounted}`
      }
      const sample = counter.sample()
      logger.warn(`${count} hydration mismatch(es) on a rendered page`, sample)
      void $fetch('/api/ssr-flag-check', {
        method: 'POST',
        body: { group, hydrationMismatches: count, sample, where },
      }).catch((error: unknown) => {
        // A report that does not arrive must not take the page with it.
        logger.warn('Failed to report a hydration mismatch', error)
      })
    })
  },
})
